import { Jobs } from 'src/@shared/types/cms/jobs'
import HeroImage from '@assets/img/carrers-hero.webp'
import AboutImage from '@assets/img/about-casper.webp'
import NiamhImage from '@assets/img/niamh-profile.webp'
import KaranImage from '@assets/img/karan-profile.webp'
import IuliaImage from '@assets/img/iulia-profile.webp'
import JoeImage from '@assets/img/joe-profile.webp'
import { Container } from 'react-bootstrap'

import CustomHead, { OpenGraphProps } from '@atk/components/head/customHead/customHead'
import Hero from '@atk/components/hero/Hero/Hero'
import ContentCenter from '@atk/components/content/ContentCenter/ContentCenter'
import Newsletter2 from '@atk/components/newsletter/Newsletter2/Newsletter2'
import CardHorizontal4 from '@atk/components/cards/CardHorizontal4/CardHorizontal4'
import AccordionComponent from '@atk/components/accordion/Accordion/Accordion'

import FramerMotion from '@atk/hooks/framerMotion'

import OpenRoles from '../OpenRoles/OpenRoles'
import RecruitmentProcess from '../RecruitmentProcess/RecruitmentProcess'
import OurMission from '../OurMission/OurMission'
import PeopleStories from '../PeopleStories/PeopleStories'

export default function CareersPage({
    jobs,
    title,
    description,
    og,
}: {
    jobs: Jobs[]
    defaultActiveKey: string
    title: string
    description: string
    og: OpenGraphProps
}) {
    const heroTitle = `Help us write blockchain’s next chapter.`

    const JoinCasperLabsTitle = `Why join <span class="text-red">Casper Labs?</span>`

    const CardHorizontal4Items = [
        {
            title: 'Competitive Salary',
            desc: 'We offer a competitive salary combined with employment in a stable, long-term-oriented, and well-founded company.',
            bgColor: '#BCFC07',
        },
        {
            title: 'Ownership Incentives',
            desc: 'Casper Labs offers long-term incentive plans in the form of both the Casper token and share options.',
            bgColor: '#AD53E5',
        },
        {
            title: 'Fully Remote',
            desc: 'Casper Labs offers a fully remote working environment that allows team members to work when, where, and how they want. ',
            bgColor: '#4589F6',
        },
        {
            title: 'Customized IT Equipment',
            desc: 'We provide a generous budget for IT equipment of your choice.',
            bgColor: '#F622E6',
        },
        {
            title: 'Flexible Time Off',
            desc: 'We understand that things happen, from sickness to just needing an extra day off to recuperate or deal with family issues.',
            bgColor: '#3EDC64',
        },
        {
            title: 'Education Budget',
            desc: 'We provide an education budget and dedicated time off to pursue the education you prefer - whatever allows you to grow is fine with us.',
            bgColor: '#F9AACA',
        },
    ]

    const AccordionItems = [
        {
            title: 'Ownership',
            desc: 'By taking ownership, we stay proactive, find the best solutions to complex problems, and don’t shy away from responsibility. It lets us stay open-minded with a big-picture perspective that contributes to the overall success of the company. We augment this by offering ownership incentive plans.',
        },
        {
            title: 'Autonomy and Flexibility',
            desc: 'Late night sessions or early mornings, the Nevada desert or Finnish forest, we want you to work where you feel inspired. Everyone has their own way of achieving excellence, and we don’t let location get in our way.',
        },
        {
            title: 'Solutions Oriented',
            desc: 'We see every problem as a latent opportunity - a solution waiting to happen. That’s why our work environment is founded on pragmatism and teamwork rather than status or competition between colleagues.',
        },
        {
            title: 'Partnership and Mutual Respect',
            desc: 'Casper Labs thrives on healthy and respectful relationships. We always assume that our peers have good intentions and are receptive to others. We are direct, but always respectful.',
        },
        {
            title: 'International Working Environment',
            desc: 'It’s no secret that diversity of thought leads to more innovative solutions. Our international environment helps to introduce new perspectives and fosters cooperation and respect among the ever-growing talent pool created by our increasingly connected world.',
        },
        {
            title: 'Personal Growth',
            desc: 'Blockchain is growing, and we’re growing along with it. We recognize that personal growth is one of the strongest sources of motivation. That’s why, along with working with like-minded and high-level professionals, we invest in coaching and support personal development activities.',
        },
    ]

    const Quotes = [
        {
            author: 'Niamh',
            quote: 'It’s exciting to work with a blockchain that can be upgraded as business needs change - this has really resonated with businesses.',
            authorImg: NiamhImage.src,
        },
        {
            author: 'Karan',
            quote: 'The culture here makes everyone feel connected - nobody is just an ID number.',
            authorImg: KaranImage.src,
        },
        {
            author: 'Iulia',
            quote: 'I’ve worked with a lot of different teams and engineers and I recognized Casper Labs strong team.',
            authorImg: IuliaImage.src,
        },
        {
            author: 'Joe',
            quote: 'I’m here because I can develop my professional skills in a positive atmosphere while working on interesting projects.',
            authorImg: JoeImage.src,
        },
    ]

    const aboutCasperTitle = `About <span class="text-red">Casper Labs</span>`
    const pageClass = `careersPage overflow-hidden `
    return (
        <div className={pageClass}>
            <CustomHead title={title} description={description} og={og}>
                <link rel="preload" href={HeroImage.src} as="image" />
            </CustomHead>
            <FramerMotion animation="fadeIn">
                <main>
                    <Hero
                        isHeader
                        customClass="heroServices pt-0 pb-0"
                        col1={6}
                        col2={6}
                        halfTitle="careers"
                        title={heroTitle}
                        desc="Casper is building the team that helps businesses work more thoughtfully, efficiently, and impactfully."
                        linkButton={{ label: 'see open positions', link: '#jobs', variant: 'secondary' }}
                        bgImage={HeroImage.src}
                    ></Hero>
                    <FramerMotion>
                        <ContentCenter
                            colWidth={10}
                            title={JoinCasperLabsTitle}
                            desc="With vast growth opportunities, projects with global impact, high-level industry professionals, and game-changing technologies, Casper Labs is an ideal destination to take the next step in your career. Join our team of builders and innovators to drive the next wave of blockchain technology!"
                            ContentCenterChild={
                                <FramerMotion>
                                    <PeopleStories list={Quotes} />
                                </FramerMotion>
                            }
                        />
                    </FramerMotion>
                    <FramerMotion>
                        <OurMission />
                    </FramerMotion>
                    <div className="ourCulture">
                        <FramerMotion>
                            <Container>
                                <AccordionComponent
                                    customClass="pb-6"
                                    sectionTitle="Our Culture"
                                    items={AccordionItems}
                                />
                            </Container>
                        </FramerMotion>
                    </div>
                    <FramerMotion y={400}>
                        <CardHorizontal4
                            hasArrow
                            customClass="benefitsSection"
                            sectionTitle="The Benefits We Offer"
                            items={CardHorizontal4Items}
                        />
                    </FramerMotion>
                    <div id="jobs">{jobs && <OpenRoles jobs={jobs} />}</div>
                    <FramerMotion>
                        <RecruitmentProcess />
                    </FramerMotion>
                    <FramerMotion>
                        <Hero
                            customClass="AboutCasperLabs p-0"
                            colLg1={6}
                            colLg2={6}
                            title={aboutCasperTitle}
                            desc="A growing number of organizations, from fast-growth companies to the public sector and Fortune 500 are choosing to build on Casper, thanks to its completely secure and future-proof architecture."
                            linkButton={{ label: 'About us', link: '/company', variant: 'secondary' }}
                            bgImage={AboutImage.src}
                        ></Hero>
                    </FramerMotion>
                    <Newsletter2 col1={5} col2={7} title="Sign up for our newsletter!" />
                </main>
            </FramerMotion>
        </div>
    )
}
